import liff from '@line/liff'
import { Link, useParams } from '@tanstack/react-router'
import { Badge } from '@wedding-tech/ui/badge'
import { Button, buttonVariants } from '@wedding-tech/ui/button'
import confetti from 'canvas-confetti'
import {
  Calendar,
  CheckCircle2,
  Home,
  MapPin,
  UtensilsCrossed,
  X,
} from 'lucide-react'
import { useEffect } from 'react'

import { useSettings } from '@/hooks/useSettings'
import { formatThaiDate } from '@/lib/date'
import { useLIFF } from '@/lib/liff/context'
import { cn } from '@/lib/utils'

interface ThankYouStepProps {
  profileData?: { fullName?: string | null } | null
  pictureUrl?: string | null
  checkInDetail?: {
    table?: string | null
    tableAssignment?: {
      tableNumber?: string | null
      seatDisplay?: string | null
      zone?: string | null
    } | null
  } | null
}

export function ThankYouStep({
  profileData,
  pictureUrl,
  checkInDetail,
}: ThankYouStepProps) {
  const params = useParams({ strict: false }) as { eventId?: string }
  const eventId = params.eventId ?? ''
  const { settings } = useSettings()
  const { isInClient } = useLIFF()

  useEffect(() => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    })
  }, [])

  const settingsData = (settings as any)?.data ?? settings ?? {}
  const eventTitle =
    settingsData.coupleNames ||
    settingsData.title ||
    settingsData.weddingTitle ||
    settingsData.name ||
    ''

  const rawDate =
    settingsData.eventDate ||
    settingsData.weddingDate ||
    settingsData.date ||
    settingsData.dateTime ||
    settingsData.rsvpDateRange?.start

  const eventDateDisplay = rawDate ? formatThaiDate(rawDate) : ''

  const tableNumber =
    checkInDetail?.table || checkInDetail?.tableAssignment?.tableNumber || null
  const seatDisplay = checkInDetail?.tableAssignment?.seatDisplay || null
  const zone = checkInDetail?.tableAssignment?.zone || null

  return (
    <div className="bg-background relative mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center space-y-6 p-6 text-center">
      {/* Success Icon Bubble */}
      <div className="bg-primary/10 text-primary mx-auto flex size-20 items-center justify-center rounded-full">
        <CheckCircle2 className="size-12" />
      </div>

      {/* Header Info */}
      <div className="space-y-2">
        {eventTitle && (
          <Badge
            variant="outline"
            className="text-primary border-primary/30 px-3 py-1 text-xs font-semibold"
          >
            {eventTitle}
          </Badge>
        )}
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          เช็กอินเรียบร้อยแล้ว
        </h1>
        {eventDateDisplay && (
          <p className="text-muted-foreground flex items-center justify-center gap-1.5 pt-0.5 text-xs">
            <Calendar className="text-primary size-3.5" />
            <span>{eventDateDisplay}</span>
          </p>
        )}
        <p className="text-muted-foreground pt-1 text-sm">
          ขอบคุณที่มาร่วมแสดงความยินดีในวันสำคัญของเรา
        </p>
      </div>

      {/* Profile & Table Card */}
      <div className="bg-card space-y-4 rounded-2xl border p-5 shadow-xs">
        {/* Profile picture & name */}
        <div className="flex flex-col items-center gap-3">
          {pictureUrl && (
            <div className="relative">
              <img
                draggable={false}
                src={pictureUrl}
                alt="Profile"
                className="border-background size-20 rounded-full border-2 object-cover shadow-sm"
              />
              <div className="bg-primary text-primary-foreground absolute -right-1 bottom-0 flex size-6 items-center justify-center rounded-full shadow-sm">
                <CheckCircle2 className="size-4" />
              </div>
            </div>
          )}
          {profileData?.fullName && (
            <div>
              <span className="text-muted-foreground text-xs font-medium">
                ยินดีต้อนรับ
              </span>
              <p className="text-foreground text-lg font-bold">
                คุณ {profileData.fullName}
              </p>
            </div>
          )}
        </div>

        {/* Table Assignment Box */}
        {tableNumber ? (
          <div className="bg-primary/5 border-primary/20 rounded-xl border p-4 text-center">
            <div className="text-muted-foreground flex items-center justify-center gap-1.5 text-xs font-semibold uppercase">
              <UtensilsCrossed className="text-primary size-3.5" />
              <span>ที่นั่งของคุณ</span>
            </div>
            <p className="text-primary mt-1 text-3xl font-extrabold tracking-tight">
              โต๊ะ {tableNumber}
              {seatDisplay && (
                <span className="text-primary/80 block text-lg font-bold">
                  ที่นั่ง {seatDisplay}
                </span>
              )}
            </p>
            {zone && (
              <p className="text-muted-foreground mt-0.5 text-xs font-medium">
                โซน {zone}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-muted/40 rounded-xl border p-4 text-center">
            <div className="text-muted-foreground flex items-center justify-center gap-1.5 text-xs font-medium">
              <MapPin className="size-3.5" />
              <span>ที่นั่งของคุณ</span>
            </div>
            <p className="text-foreground mt-1 text-sm font-semibold">
              เจ้าหน้าที่จะแจ้งโต๊ะที่หน้างาน
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2.5 pt-2">
        <Link
          to="/e/$eventId/map"
          params={{ eventId }}
          className={cn(
            buttonVariants({ variant: 'outline', size: 'lg' }),
            'w-full font-semibold',
          )}
        >
          <MapPin className="mr-1.5 size-4" />
          ดูแผนผังที่นั่ง
        </Link>

        <Link
          to="/e/$eventId"
          params={{ eventId }}
          className={cn(
            buttonVariants({ variant: 'default', size: 'lg' }),
            'w-full font-semibold',
          )}
        >
          <Home className="mr-1.5 size-4" />
          ไปยังหน้าหลัก
        </Link>

        {isInClient && (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="text-muted-foreground hover:text-foreground w-full font-medium"
            onClick={() => liff.closeWindow()}
          >
            <X className="mr-1.5 size-4" />
            ปิดหน้าต่าง
          </Button>
        )}
      </div>
    </div>
  )
}
